import React, { useCallback, useEffect, useState } from 'react';
import { apiListUsers, apiCreateUser, apiResetQuota, apiAdjustQuota, apiResetPassword, apiDeleteUser, AuthUser } from '../../services/server-api';
import './AccountAdmin.css';

// 账号管理（仅管理员）：本系统无注册入口，账号只在这里创建
const AccountAdmin: React.FC = () => {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // 新建表单
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<'user' | 'admin'>('user');
  const [newQuota, setNewQuota] = useState('50');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiListUsers();
      setUsers(result.users);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载账号列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 3000);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword || creating) return;
    setCreating(true);
    setError(null);
    try {
      const quota = Math.max(1, Math.floor(Number(newQuota) || 50));
      await apiCreateUser(newUsername.trim(), newPassword, newRole, quota);
      setNewUsername('');
      setNewPassword('');
      setNewRole('user');
      setNewQuota('50');
      flash(`账号「${newUsername.trim()}」创建成功，提问额度 ${quota}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const handleResetQuota = async (username: string) => {
    try {
      await apiResetQuota(username);
      flash(`账号「${username}」已用次数已清零`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '重置失败');
    }
  };

  const handleAdjustQuota = async (username: string, current: number) => {
    const input = window.prompt(`调整账号「${username}」的提问总额度（当前 ${current}）：`, String(current + 50));
    if (input === null) return;
    const value = Math.floor(Number(input));
    if (!Number.isFinite(value) || value < 1) {
      setError('配额需为正整数');
      return;
    }
    try {
      await apiAdjustQuota(username, value);
      flash(`账号「${username}」提问额度已调整为 ${value}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '调整失败');
    }
  };

  const handleResetPassword = async (username: string) => {
    const password = window.prompt(`为账号「${username}」设置新密码（至少6位）：`);
    if (!password) return;
    try {
      await apiResetPassword(username, password);
      flash(`账号「${username}」密码已重置`);
    } catch (err) {
      setError(err instanceof Error ? err.message : '重置失败');
    }
  };

  const handleDelete = async (username: string) => {
    if (!window.confirm(`确认删除账号「${username}」？其历史提问记录将一并删除。`)) return;
    try {
      await apiDeleteUser(username);
      flash(`账号「${username}」已删除`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败');
    }
  };

  return (
    <div className="account-admin-page">
      <div className="page-header">
        <h2>账号管理</h2>
        <p className="page-subtitle">本系统无注册入口，账号由管理员在此创建；每账号默认 50 个AI策略提问额度，不够可随时调整</p>
      </div>

      {error && <p className="account-error">{error}</p>}
      {notice && <p className="account-notice">{notice}</p>}

      <div className="account-card">
        <h3>添加账号</h3>
        <form className="account-create-form" onSubmit={handleCreate}>
          <input
            type="text"
            placeholder="用户名（2-32位字母/数字/下划线）"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
          />
          <input
            type="password"
            placeholder="初始密码（至少6位）"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <select value={newRole} onChange={(e) => setNewRole(e.target.value as 'user' | 'admin')}>
            <option value="user">普通用户</option>
            <option value="admin">管理员</option>
          </select>
          <input
            type="number"
            min={1}
            placeholder="提问额度（默认50）"
            value={newQuota}
            onChange={(e) => setNewQuota(e.target.value)}
            className="quota-input"
          />
          <button type="submit" className="account-btn" disabled={creating || !newUsername.trim() || !newPassword}>
            {creating ? '创建中...' : '创建账号'}
          </button>
        </form>
      </div>

      <div className="account-card">
        <h3>账号列表{loading ? '（加载中...）' : `（${users.length}）`}</h3>
        <table className="account-table">
          <thead>
            <tr>
              <th>用户名</th>
              <th>角色</th>
              <th>提问配额</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.username}>
                <td>{user.username}</td>
                <td>{user.role === 'admin' ? '管理员' : '普通用户'}</td>
                <td>
                  <span className={user.remainingQuota === 0 ? 'quota-exhausted' : ''}>
                    已用 {user.usedCount} / 总额度 {user.questionLimit}
                  </span>
                </td>
                <td>{user.createdAt.slice(0, 10)}</td>
                <td className="account-actions">
                  <button className="account-action-btn" onClick={() => handleAdjustQuota(user.username, user.questionLimit)}>
                    调整配额
                  </button>
                  <button className="account-action-btn" onClick={() => handleResetQuota(user.username)}>
                    清零已用
                  </button>
                  <button className="account-action-btn" onClick={() => handleResetPassword(user.username)}>
                    重置密码
                  </button>
                  {user.username !== 'admin' && (
                    <button className="account-action-btn danger" onClick={() => handleDelete(user.username)}>
                      删除
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountAdmin;
